import { useState } from "react";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import {
  ArticleOutlined as ClaimIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FactCheckOutlined as ReconciliationIcon,
  BarChartOutlined as ReportsIcon
} from "../icons";
import { string } from "../../constants/string";
import type { SidebarProp } from "../Sidebar";

type ClaimsNavProps = {
  props: SidebarProp;
  effectiveRole: unknown;
  closeDrawer: () => void;
};

/**
 * The claims side of the navigation.
 *
 * Split out of Sidebar.tsx, which rendered both products' trees in one
 * 1,255-line component. The collapse state for each section lives here rather
 * than in the parent, because nothing outside this tree ever reads it.
 *
 * Note the two different gates below. The claims sections answer to
 * `canSeeClaims` (i.e. "not an IFAS user"), but Reports and Finance are gated
 * on hardcoded role strings — `claim_manager` and `finance` — rather than on a
 * capability from utils/ifasAccess.ts. That was the behaviour before this
 * split and is preserved exactly; it is called out because a role rename would
 * silently empty those sections and nothing would fail.
 */
export default function ClaimsNav({
  props,
  effectiveRole,
  closeDrawer,
}: ClaimsNavProps) {
  const {
    onDeathClaimClick,
    onTenPercentClick,
    onExecutiveReportsClick,
  } = props;

  const [open, setOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  const handleToggle = () => setOpen(!open);
  const handleReportsToggle = () => setReportsOpen(!reportsOpen);

  return (
    <>
              <>
                <ListItemButton onClick={handleToggle}>
                  <ListItemIcon sx={{ minWidth: 46 }}>
                    <ClaimIcon
                      sx={{
                        color: "rgba(0, 0, 0, 0.62)",
                        fontSize: "26px",
                        ml: 0.5,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={string.sidebar.claims}
                    slotProps={{
                      primary: { sx: { color: "text.primary", fontWeight: 500 } },
                    }}
                  />
                  {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton
                      sx={{ ml: 6, borderBottom: "2px solid #DFDDDD" }}
                      onClick={() => {
                        onTenPercentClick?.();
                        closeDrawer();
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <ClaimIcon
                          sx={{
                            color: "rgba(0, 0, 0, 0.62)",
                            fontSize: "22px",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={string.sidebar.percentClaim}
                        slotProps={{
                          primary: {
                            sx: { color: "text.primary", fontWeight: 500 },
                          },
                        }}
                      />
                    </ListItemButton>
                    <ListItemButton
                      sx={{ ml: 6 }}
                      onClick={() => {
                        onDeathClaimClick?.();
                        closeDrawer();
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <ClaimIcon
                          sx={{
                            color: "rgba(0, 0, 0, 0.62)",
                            fontSize: "22px",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={string.sidebar.deathClaim}
                        slotProps={{
                          primary: {
                            sx: { color: "text.primary", fontWeight: 500 },
                          },
                        }}
                      />
                    </ListItemButton>
                  </List>
                </Collapse>
              </>
      {(effectiveRole === "claim_manager" || effectiveRole === "finance") && (
        <>
          <>
            <ListItemButton onClick={handleReportsToggle}>
              <ListItemIcon sx={{ minWidth: 46 }}>
                <ReportsIcon
                  sx={{
                    color: "rgba(0, 0, 0, 0.62)",
                    fontSize: "26px",
                    ml: 0.5,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Reports"
                slotProps={{
                  primary: { sx: { color: "text.primary", fontWeight: 500 } },
                }}
              />
              {reportsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={reportsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* <ListItemButton
              sx={{ ml: 6, borderBottom: "2px solid #DFDDDD" }}
              onClick={() => {
                onMonthlyReportsClicked?.();
                closeDrawer();
              }}
            >
               <ListItemIcon sx={{ minWidth: 40 }}>
                    <CalendarMonthOutlined
                      sx={{ color: "rgba(0, 0, 0, 0.62)", fontSize: "22px" }}
                    />
                  </ListItemIcon>
              <ListItemText
                primary="Monthly"
               slotProps={{
                      primary: {
                        sx: { color: "text.primary", fontWeight: 500 },
                      },
                    }}
              />
            </ListItemButton> */}
                <ListItemButton
                  sx={{ ml: 6 }}
                  onClick={() => {
                    onExecutiveReportsClick?.();
                    closeDrawer();
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ReconciliationIcon
                      sx={{
                        color: "rgba(0, 0, 0, 0.62)",
                        fontSize: "22px",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Executive (Reconciliation)"
                    slotProps={{
                      primary: {
                        sx: { color: "text.primary", fontWeight: 500 },
                      },
                    }}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </>

        </>
      )}
    </>
  );
}
